import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT, safetyMargins } from '@bedrock-rdk/constants';
import { getProgress } from '../progress/progress.utils';
import {
  areContentItemsEqual,
  areContextsEqual,
  getTemplateEntry,
  ICON_POSITION,
  makeTextPatch,
} from '../utils/templates';
import { TRANSPARENT } from '../utils/colors';
import ImageShader from '../shaders/image.shader';
import FocusShader from '../shaders/focus.shader';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import {
  defaultContext,
  getInitialStyle,
  unFocusedStyle,
  TEXT_LEFT_PADDING,
  colors,
  getIconViewBox,
  getCentralIconViewBox,
  ICON_PADDING_VERTICAL,
  PROGRESS_HEIGHT,
  getIcon,
  getFocusedStyle,
} from './horizontalCard.style';
import {
  sizes,
  templates,
  imageOptions,
  ICONS_TYPES,
  DISPLAY_EXTRA_TITLE,
  DISPLAY_DETAILS,
} from './horizontalCard.constants';

/**
 * Item used among other things on the video player to view related contents. It also used for the management of downloaded videos.
 *
 * @version 2.7.1
 * @see https://zeroheight.com/726d5c282/v/latest/p/763e44-horizontalcard--v271/b/153d1d
 */
export default class HorizontalCard extends Lightning.Component {
  /* istanbul ignore next */
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        Gradient: {
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        ExtraFlexContainer: {
          CentralIcon: {
            type: ICONS_TYPES[ICON_POSITION.CENTER],
          },
          ExtraHighlight: {},
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        FlexContainer: {
          Title: {},
          ExtraTitle: {},
          Highlight: {},
          Description: {},
          Details: {},
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _attach() {
    const image = this.tag('Image');

    image.on('txLoaded', () => {
      image.setSmooth('alpha', 1);
    });
  }

  _detach() {
    this.tag('Image').removeAllListeners('txLoaded');
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    sideGlass,
    colorScheme = defaultContext.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    const progress = getProgress(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      // A three level patch object to move one number: the shader is already there, so it is written directly
      this.tag('Image').shader.progress = progress;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, this._style.screenSize);
    this._itemTemplate = template;

    const { title, extraTitle, details, highlight, description, extraHighlight, pictos, image } = this._item;
    const { wordWrapWidth } = this;
    const textColor = colors.text[colorScheme];
    const imageUrl = (image && image.src) || '';

    this.patch({
      w: this._config.w,
      h: this._config.h,
      Image: {
        alpha: 0.0001,
        src: imageUrl,
        shader: {
          progress,
        },
      },
      ExtraFlexContainer: {
        visible: sideGlass,
        CentralIcon: {
          iconData: {
            picto: getIcon(pictos, ICON_POSITION.CENTER, sideGlass),
            viewBox: getCentralIconViewBox(this._style.screenSize),
          },
        },
        ExtraHighlight: makeTextPatch(extraHighlight, wordWrapWidth, textColor),
      },
      TopLeftIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_LEFT, sideGlass),
          viewBox: getIconViewBox(this._style.screenSize),
        },
      },
      TopRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_RIGHT, sideGlass),
          viewBox: getIconViewBox(this._style.screenSize),
        },
      },
      BottomRightIcon: {
        y: this._config.h - ICON_PADDING_VERTICAL - PROGRESS_HEIGHT,
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT, sideGlass),
          viewBox: getIconViewBox(this._style.screenSize),
        },
      },
      FlexContainer: {
        // NOTE: needs to use rect property to justifying flex items in flex container
        rect: true,
        color: TRANSPARENT,
        flex: {
          direction: 'column',
          justifyContent: 'center',
        },
        Title: makeTextPatch(title, wordWrapWidth, textColor),
        ExtraTitle: makeTextPatch(DISPLAY_EXTRA_TITLE ? extraTitle : '', wordWrapWidth, textColor),
        Highlight: makeTextPatch(highlight, wordWrapWidth, textColor),
        Description: makeTextPatch(description, wordWrapWidth, textColor),
        Details: makeTextPatch(DISPLAY_DETAILS ? details : '', wordWrapWidth, textColor),
      },
    });
  }

  get wordWrapWidth() {
    const renderingContext = this._renderingContext || RENDERING_CONTEXT.GENERAL;

    const { screenSize } = this._style;
    const {
      PRIMARY: { width: imageWidth },
    } = getTemplateEntry(imageOptions, this._itemTemplate, renderingContext, screenSize);
    const padding = TEXT_LEFT_PADDING[screenSize];
    const width = this._config && this._config.w !== undefined ? this._config.w : this.finalW;
    const safetyMargin = safetyMargins.vertical[screenSize];

    return width - imageWidth - padding - safetyMargin;
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

HorizontalCard.Templates = templates;
HorizontalCard.Sizes = sizes;
HorizontalCard.ImageOptions = imageOptions;
